import { useState } from "react";
import "./styles/textarea.sass";
import { useRef } from "react";
import { Controller } from "react-hook-form";

interface IProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    characterLimit?: number;
    name: string;
    control: any;
}

/**
 * A textarea input that can accept a specific amount of characters.
 * If specified, it will indicate the current amount of characters and display the limit.
 */
const Textarea = ({ name, characterLimit, control, ...rest }: IProps) => {
    const [characterAmount, setCharacterAmount] = useState(0);
    const indicatorRef = useRef<null | SVGCircleElement>(null);
    /*
     * Updates the number of characters the user has typed as well as the global indicator upon change, blur and focus.
     * If the characterLimit wasn't set, then it returns void
     * @param e	The target of the event
     */
    function updateCharactersLeft(e: React.ChangeEvent<HTMLTextAreaElement>) {
        if (characterLimit == undefined) {
            return;
        }
        const value = e.target.value;
        setCharacterAmount(value.length);

        if (indicatorRef.current == undefined) {
            return;
        }
        const dashOffset = calculateDashOffset();
        indicatorRef.current.style.strokeDashoffset = dashOffset.toString();
    }
    /*
     * Given the % that characterAmount is to characterLimit, determine that same % for the indicator's path length (126)
     */
    function calculateDashOffset() {
        const charPercentage = (characterAmount / characterLimit!) * 100;
        const pathLength = (126 * charPercentage) / 100;
        return pathLength;
    }

    return (
        <div className="textarea__wrapper">
            <Controller
                name={name}
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                    <textarea
                        className="input__input input__textarea"
                        id={name}
                        maxLength={characterLimit}
                        onChange={(e) => {
                            updateCharactersLeft(e);
                            onChange(e);
                        }}
                        onFocus={(e) => {
                            updateCharactersLeft(e);
                        }}
                        onBlur={(e) => {
                            updateCharactersLeft(e);
                            onBlur();
                        }}
                        value={value}
                        {...rest}
                    />
                )}
            />
            {characterLimit && (
                <span className="textarea__indicator">
                    <svg viewBox="25 25 50 50" height="25" width="25">
                        <circle
                            ref={indicatorRef}
                            cx="50"
                            cy="50"
                            r="20"
                            fill="none"
                            stroke="#70c542"
                            strokeWidth="5"
                        />
                    </svg>
                    {characterLimit - characterAmount}
                </span>
            )}
        </div>
    );
};

export default Textarea;
